/**
 * State + thunks for the Creator Settings → Configurations tab.
 *
 * Talks to /api/creator/configurations via creatorConfigurationsAPI;
 * server sanitises + whitelists keys (see CONFIG_SCHEMA on the server).
 *
 * Save-all form rather than per-key optimistic toggles — typed strings
 * are edited in a FinalForm and persisted on submit. The duck tracks
 * load + save status; the page is a pure consumer.
 */

import { creatorConfigurationsAPI } from '../../api';
import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const FETCH_CONFIG_REQUEST = 'app/CreatorSettingsConfigurationsPage/FETCH_CONFIG_REQUEST';
export const FETCH_CONFIG_SUCCESS = 'app/CreatorSettingsConfigurationsPage/FETCH_CONFIG_SUCCESS';
export const FETCH_CONFIG_ERROR = 'app/CreatorSettingsConfigurationsPage/FETCH_CONFIG_ERROR';

export const SAVE_CONFIG_REQUEST = 'app/CreatorSettingsConfigurationsPage/SAVE_CONFIG_REQUEST';
export const SAVE_CONFIG_SUCCESS = 'app/CreatorSettingsConfigurationsPage/SAVE_CONFIG_SUCCESS';
export const SAVE_CONFIG_ERROR = 'app/CreatorSettingsConfigurationsPage/SAVE_CONFIG_ERROR';

// Sender domain
export const DOMAIN_REQUEST = 'app/CreatorSettingsConfigurationsPage/DOMAIN_REQUEST';
export const DOMAIN_SUCCESS = 'app/CreatorSettingsConfigurationsPage/DOMAIN_SUCCESS';
export const DOMAIN_ERROR = 'app/CreatorSettingsConfigurationsPage/DOMAIN_ERROR';

// ================ Reducer ================ //

const initialState = {
  config: null,
  fetchInProgress: false,
  fetchLoaded: false,
  fetchError: null,
  saveInProgress: false,
  saveError: null,
  savedAt: null,
  // Sender domain
  senderDomain: null,
  domainInProgress: false,
  domainError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_CONFIG_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_CONFIG_SUCCESS:
      return { ...state, fetchInProgress: false, fetchLoaded: true, config: payload.config };
    case FETCH_CONFIG_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case SAVE_CONFIG_REQUEST:
      return { ...state, saveInProgress: true, saveError: null };
    case SAVE_CONFIG_SUCCESS:
      return {
        ...state,
        saveInProgress: false,
        config: payload.config,
        savedAt: Date.now(),
      };
    case SAVE_CONFIG_ERROR:
      return { ...state, saveInProgress: false, saveError: payload };

    case DOMAIN_REQUEST:
      return { ...state, domainInProgress: true, domainError: null };
    case DOMAIN_SUCCESS:
      return { ...state, domainInProgress: false, senderDomain: payload.senderDomain };
    case DOMAIN_ERROR:
      return { ...state, domainInProgress: false, domainError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchConfigRequest = () => ({ type: FETCH_CONFIG_REQUEST });
const fetchConfigSuccess = config => ({ type: FETCH_CONFIG_SUCCESS, payload: { config } });
const fetchConfigError = e => ({ type: FETCH_CONFIG_ERROR, payload: e });

const saveConfigRequest = () => ({ type: SAVE_CONFIG_REQUEST });
const saveConfigSuccess = config => ({ type: SAVE_CONFIG_SUCCESS, payload: { config } });
const saveConfigError = e => ({ type: SAVE_CONFIG_ERROR, payload: e });

// ================ Thunks ================ //

export const fetchCreatorConfig = () => async dispatch => {
  dispatch(fetchConfigRequest());
  try {
    const response = await creatorConfigurationsAPI.getConfig();
    const body = response?.data ?? response ?? {};
    const data = body?.data ?? body;
    dispatch(fetchConfigSuccess(data?.config || {}));
    return data?.config;
  } catch (e) {
    dispatch(fetchConfigError(storableError(e)));
    throw e;
  }
};

export const saveCreatorConfig = patch => async dispatch => {
  dispatch(saveConfigRequest());
  try {
    const response = await creatorConfigurationsAPI.updateConfig(patch);
    const body = response?.data ?? response ?? {};
    const data = body?.data ?? body;
    dispatch(saveConfigSuccess(data?.config));
    return data?.config;
  } catch (e) {
    dispatch(saveConfigError(storableError(e)));
    throw e;
  }
};

// ── Sender domain ──

const domainRequest = () => ({ type: DOMAIN_REQUEST });
const domainSuccess = senderDomain => ({ type: DOMAIN_SUCCESS, payload: { senderDomain } });
const domainError = e => ({ type: DOMAIN_ERROR, payload: e });

// Every sender-domain endpoint returns { data: { senderDomain } }; this
// unwraps whichever nesting the httpClient produced.
const pluckDomain = response => {
  const body = response?.data ?? response ?? {};
  const data = body?.data ?? body;
  return data?.senderDomain || null;
};

const domainThunk = apiCall => async dispatch => {
  dispatch(domainRequest());
  try {
    const senderDomain = pluckDomain(await apiCall());
    dispatch(domainSuccess(senderDomain));
    return senderDomain;
  } catch (e) {
    dispatch(domainError(storableError(e)));
    throw e;
  }
};

export const fetchSenderDomain = () =>
  domainThunk(() => creatorConfigurationsAPI.getSenderDomain());
export const connectSenderDomain = domain =>
  domainThunk(() => creatorConfigurationsAPI.connectSenderDomain(domain));
export const verifySenderDomain = () =>
  domainThunk(() => creatorConfigurationsAPI.verifySenderDomain());
export const setSenderFromEmail = fromEmail =>
  domainThunk(() => creatorConfigurationsAPI.setSenderFromEmail(fromEmail));
export const disconnectSenderDomain = () =>
  domainThunk(() => creatorConfigurationsAPI.disconnectSenderDomain());

// ================ loadData ================ //

export const loadData = () => dispatch =>
  Promise.all([dispatch(fetchCreatorConfig()), dispatch(fetchSenderDomain())]);
